import { useState } from "react";
import { IbanElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const IBAN_ELEMENT_OPTIONS = {
  supportedCountries: ["SEPA"],
  style: {
    base: {
      fontFamily: "'Inter', sans-serif",
      fontSize: "15px",
      color: "#0f172a",
      "::placeholder": { color: "#94a3b8" },
      iconColor: "#059669",
    },
    invalid: { color: "#ef4444", iconColor: "#ef4444" },
  },
};


function computeNextOccurrence(day) {
  const now = new Date();
  const candidate = new Date(now.getFullYear(), now.getMonth(), day);
  candidate.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (candidate <= today) {
    candidate.setMonth(candidate.getMonth() + 1);
  }
  return candidate.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function todayFrench() {
  return new Date().toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

const SepaPaymentForm = ({
  clientSecret,
  monthlyAmount,
  remainingAmount,
  member,
  associationName: associationNameProp,
  // Reçoit { setup_intent_id, consent_date, debit_day } après signature du
  // mandat et renvoie les données du résultat (création de l'abonnement).
  // Fourni par chaque parcours appelant (espace membre / lien admin).
  onConfirmMandate,
  onSuccess,
  onError,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [holderName, setHolderName] = useState(
    `${member?.first_name || ""} ${member?.last_name || ""}`.trim()
  );
  const [email, setEmail] = useState(member?.email || "");
  const [ibanComplete, setIbanComplete] = useState(false);
  const [ibanError, setIbanError] = useState(null);
  const [mandateAccepted, setMandateAccepted] = useState(false);
  const [debitDay, setDebitDay] = useState(1);
  const [dayPickerOpen, setDayPickerOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const canSubmit = ibanComplete && !ibanError && mandateAccepted && holderName.trim() && email.trim() && !loading;

  const associationName = associationNameProp || member?.association?.name || "votre association";

  const handleIbanChange = (e) => {
    setIbanComplete(e.complete);
    setIbanError(e.error ? e.error.message : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (!stripe || !elements) {
      toast.error("Le formulaire de paiement n'est pas encore prêt. Rechargez la page.");
      return;
    }

    setLoading(true);

    let confirmResult;
    try {
      confirmResult = await stripe.confirmSepaDebitSetup(clientSecret, {
        payment_method: {
          sepa_debit: elements.getElement(IbanElement),
          billing_details: {
            name: holderName.trim(),
            email: email.trim(),
          },
        },
      });
    } catch (err) {
      setLoading(false);
      if (onError) onError(err);
      return;
    }

    const { error, setupIntent } = confirmResult;

    if (error) {
      setLoading(false);
      if (onError) onError(error);
      return;
    }

    try {
      const data = await onConfirmMandate({
        setup_intent_id: setupIntent.id,
        consent_date: new Date().toISOString(),
        debit_day: debitDay,
      });
      if (onSuccess) onSuccess(data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Une erreur est survenue lors de la création de l'abonnement.";
      toast.error(msg);
      if (onError) onError(new Error(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">

      {/* Info banner */}
      <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 space-y-0.5">
        <p className="text-sm text-slate-700">
          Vous serez prélevé de{" "}
          <strong>
            {(monthlyAmount || 0).toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            €
          </strong>{" "}
          par mois, pendant 12 mois
          {remainingAmount != null && (
            <>
              {" "}
              (solde de{" "}
              <strong>
                {remainingAmount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </strong>{" "}
              couvert intégralement)
            </>
          )}
        </p>
        <p className="text-xs text-slate-400">
          S'arrête automatiquement après la 12ᵉ mensualité — aucune reconduction pour l'exercice suivant.
        </p>
      </div>

      {/* Titulaire */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
          Titulaire du compte
        </label>
        <input
          type="text"
          value={holderName}
          onChange={(e) => setHolderName(e.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all"
          placeholder="Prénom Nom"
          required
        />
      </div>

      {/* Email */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
          Adresse e-mail
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all"
          placeholder="exemple@domaine.fr"
          required
        />
      </div>

      {/* IBAN */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
          IBAN
        </label>
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3.5 focus-within:border-emerald-500 focus-within:ring-1 focus-within:ring-emerald-500 transition-all">
          <IbanElement options={IBAN_ELEMENT_OPTIONS} onChange={handleIbanChange} />
        </div>
        {ibanError && (
          <p className="mt-2 text-xs text-red-500">{ibanError}</p>
        )}
      </div>

      {/* Jour de prélèvement */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
          Jour de prélèvement mensuel
        </label>
        <button
          type="button"
          onClick={() => setDayPickerOpen((o) => !o)}
          className="w-full flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 hover:border-emerald-400 transition-all"
        >
          <span>
            Le <strong>{debitDay}</strong> de chaque mois
          </span>
          <svg
            className={`h-4 w-4 text-slate-400 transition-transform ${dayPickerOpen ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {dayPickerOpen && (
          <div className="mt-1 rounded-xl border border-slate-200 bg-white p-3 shadow-md">
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => { setDebitDay(day); setDayPickerOpen(false); }}
                  className={`aspect-square rounded-lg text-sm font-medium transition-all ${
                    debitDay === day
                      ? "bg-emerald-600 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="mt-1.5 text-xs text-slate-400">
          Premier prélèvement le{" "}
          <span className="font-medium text-slate-500">{computeNextOccurrence(debitDay)}</span>
        </p>
      </div>

      {/* Separator */}
      <hr className="border-slate-100" />

      {/* Mandat SEPA */}
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-500 space-y-2">
        <p className="font-semibold text-slate-700 text-sm">Mandat de prélèvement SEPA</p>
        <p className="leading-relaxed">
          En cochant cette case, vous autorisez{" "}
          <strong className="text-slate-700">{associationName}</strong> à envoyer des instructions à votre
          banque pour débiter votre compte, et votre banque à débiter votre compte conformément aux
          instructions reçues. Vous bénéficiez d&apos;un droit au remboursement par votre banque selon
          les conditions décrites dans la convention que vous avez passée avec elle. Toute demande de
          remboursement doit être présentée dans les 8 semaines suivant la date de débit.
        </p>
        <div className="pt-1 space-y-1 text-slate-400">
          <p>
            <span className="font-medium">Référence unique mandat (RUM) :</span> générée par Stripe
          </p>
          <p>
            <span className="font-medium">Date de signature :</span> {todayFrench()}
          </p>
        </div>
      </div>

      {/* Checkbox */}
      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={mandateAccepted}
          onChange={(e) => setMandateAccepted(e.target.checked)}
          className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
        />
        <span className="text-xs text-slate-600 leading-relaxed group-hover:text-slate-900 transition-colors">
          J&apos;autorise ce prélèvement mensuel et accepte les conditions du mandat SEPA ci-dessus.
        </span>
      </label>

      {/* CTA */}
      <button
        type="submit"
        disabled={!canSubmit}
        className="w-full flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3.5 text-sm transition-colors"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Traitement…
          </>
        ) : (
          "Valider le mandat →"
        )}
      </button>

      {/* Security badge */}
      <div className="flex items-start gap-2 text-xs text-slate-400">
        <ShieldCheck className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
        <p className="font-light leading-relaxed">
          Paiement sécurisé via Stripe. Vos données ne sont jamais transmises à Hafani.
        </p>
      </div>
    </form>
  );
};

export default SepaPaymentForm;
